/**
 * useAudioRecording - Hook para gravação de áudio com Opus codec
 * Usa opus-recorder para gravar áudio em formato OGG compatível com WhatsApp
 */

import { useState, useRef, useCallback, useEffect } from 'react';

interface UseAudioRecordingOptions {
  /** Callback quando gravação é concluída */
  onRecordingComplete?: (audioBlob: Blob, duration: number) => void;
  /** Callback quando ocorre erro */
  onError?: (error: Error) => void;
  /** Duração máxima em segundos (default: 300s = 5min) */
  maxDuration?: number;
}

export interface AudioRecordingData {
  blob: Blob;
  url: string;
  duration: number;
  size: number;
}

/**
 * Hook para gravação de áudio usando Opus Recorder
 * Gera áudio em formato Opus/OGG compatível com WhatsApp
 *
 * @example
 * ```tsx
 * const { isRecording, duration, startRecording, stopRecording, audioData } = useAudioRecording({
 *   onRecordingComplete: (blob, duration) => {
 *     console.log('Audio recorded:', blob, duration);
 *   },
 * });
 * ```
 */
export const useAudioRecording = (options: UseAudioRecordingOptions = {}) => {
  const { onRecordingComplete, onError, maxDuration = 300 } = options;

  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioData, setAudioData] = useState<AudioRecordingData | null>(null);
  const [isSupported, setIsSupported] = useState(false);

  const recorderRef = useRef<any>(null);
  const startTimeRef = useRef<number>(0);
  const durationIntervalRef = useRef<number | null>(null);
  const audioBlobRef = useRef<Blob | null>(null);

  /**
   * Verificar suporte ao Opus Recorder
   */
  useEffect(() => {
    const checkSupport = () => {
      const supported = !!(
        typeof navigator !== 'undefined' &&
        navigator.mediaDevices &&
        typeof navigator.mediaDevices.getUserMedia === 'function' &&
        window.Recorder &&
        typeof window.Recorder.isRecordingSupported === 'function' &&
        window.Recorder.isRecordingSupported()
      );
      setIsSupported(supported);

      if (!supported) {
        console.warn('⚠️ Opus Recorder não está disponível ou não é suportado neste navegador');
      }
    };

    // Aguardar um pouco para garantir que o script foi carregado
    const timer = setTimeout(checkSupport, 100);
    return () => clearTimeout(timer);
  }, []);

  /**
   * Atualiza duração durante gravação
   */
  const updateDuration = useCallback(() => {
    if (startTimeRef.current > 0) {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setDuration(elapsed);

      // Auto-stop ao atingir duração máxima
      if (elapsed >= maxDuration) {
        console.log(`⏱️ Duração máxima atingida (${maxDuration}s), parando gravação`);
        stopRecording();
      }
    }
  }, [maxDuration]);

  /**
   * Inicia gravação de áudio com Opus codec
   */
  const startRecording = useCallback(async () => {
    try {
      if (!isSupported) {
        throw new Error('Opus Recorder não está disponível neste navegador');
      }

      console.log('🎤 Solicitando permissão de microfone...');

      // Solicitar permissão de microfone
      await navigator.mediaDevices.getUserMedia({ audio: true });

      console.log('✅ Permissão de microfone concedida');

      // Configurar Opus Recorder (mesma config das implementações antigas)
      const options: any = {
        monitorGain: 0,              // Sem monitor de áudio
        recordingGain: 1,            // Ganho de gravação padrão
        numberOfChannels: 1,         // Mono (WhatsApp aceita mono)
        encoderSampleRate: 48000,    // 48kHz (padrão WhatsApp)
        encoderPath: '/assets/voice/encoderWorker.min.js',
      };

      console.log('🔧 Inicializando Opus Recorder com opções:', options);

      const recorder = new window.Recorder!(options);
      recorderRef.current = recorder;

      // Event: ondataavailable
      recorder.ondataavailable = (typedArray: Blob) => {
        const audioBlob = new Blob([typedArray], { type: 'audio/ogg' });
        audioBlobRef.current = audioBlob;

        console.log('🎵 Audio Opus/OGG criado:', {
          size: audioBlob.size,
          type: audioBlob.type,
          duration: `${duration}s`
        });
      };

      // Iniciar gravação
      recorder.start();
      setIsRecording(true);
      startTimeRef.current = Date.now();

      // Iniciar contador de duração
      durationIntervalRef.current = window.setInterval(updateDuration, 1000);

      console.log('🎤 Gravação iniciada com Opus codec (audio/ogg)');
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to start recording');
      console.error('❌ Erro ao iniciar gravação:', err);
      onError?.(err);
    }
  }, [isSupported, onError, updateDuration, duration]);

  /**
   * Para gravação de áudio
   */
  const stopRecording = useCallback(() => {
    if (recorderRef.current && isRecording) {
      console.log('⏹️ Parando gravação...');

      recorderRef.current.stop();
      setIsRecording(false);

      // Limpar interval
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }

      // Aguardar processamento do blob (ondataavailable é assíncrono)
      setTimeout(() => {
        if (audioBlobRef.current) {
          const audioBlob = audioBlobRef.current;
          const audioUrl = URL.createObjectURL(audioBlob);
          const finalDuration = duration;

          const data: AudioRecordingData = {
            blob: audioBlob,
            url: audioUrl,
            duration: finalDuration,
            size: audioBlob.size,
          };

          setAudioData(data);
          onRecordingComplete?.(audioBlob, finalDuration);

          console.log('✅ Gravação concluída:', {
            duration: `${finalDuration}s`,
            size: `${(audioBlob.size / 1024).toFixed(2)} KB`,
            type: audioBlob.type,
            url: audioUrl
          });
        } else {
          console.warn('⚠️ Nenhum áudio foi gravado');
        }
      }, 100);
    }
  }, [isRecording, duration, onRecordingComplete]);

  /**
   * Cancela gravação (sem salvar)
   */
  const cancelRecording = useCallback(() => {
    if (recorderRef.current && isRecording) {
      console.log('🗑️ Cancelando gravação...');

      recorderRef.current.stop();
      setIsRecording(false);
      setDuration(0);
      setAudioData(null);
      audioBlobRef.current = null;

      // Limpar interval
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }

      console.log('✅ Gravação cancelada');
    }
  }, [isRecording]);

  /**
   * Limpa dados de áudio gravado
   */
  const clearAudio = useCallback(() => {
    if (audioData?.url) {
      URL.revokeObjectURL(audioData.url);
    }
    setAudioData(null);
    setDuration(0);
    audioBlobRef.current = null;

    console.log('🧹 Dados de áudio limpos');
  }, [audioData]);

  /**
   * Restaura (ou limpa) áudio FINALIZADO de um rascunho por conversa.
   * NÃO revoga object URLs — o rascunho da conversa anterior continua
   * referenciando a URL stashada no chatDraftStore (clearAudio revoga;
   * este método existe justamente para trocar de conversa sem revogar).
   */
  const restoreAudio = useCallback((data: AudioRecordingData | null) => {
    setAudioData(data);
    setDuration(data?.duration ?? 0);
    audioBlobRef.current = data?.blob ?? null;
  }, []);

  /**
   * Formata duração em MM:SS
   */
  const formatDuration = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  return {
    /** Se está gravando */
    isRecording,
    /** Duração em segundos */
    duration,
    /** Dados do áudio gravado */
    audioData,
    /** Inicia gravação */
    startRecording,
    /** Para gravação */
    stopRecording,
    /** Cancela gravação */
    cancelRecording,
    /** Limpa dados de áudio */
    clearAudio,
    /** Restaura (ou limpa) áudio finalizado de um rascunho por conversa */
    restoreAudio,
    /** Formata duração */
    formatDuration,
    /** Se navegador suporta */
    isSupported,
  };
};
